import {
  useCreateEncryptionContext,
  useInitiateSealdIdentity,
} from "apollo/hooks/mutations";
import RootPage from "containers/RootPage";
import {
  ProviderSearchContext,
  ProviderSearchProvider,
  TestProviderSearchProvider,
  useProviderSearchContext,
} from "context/ProviderSearchContext";
import {
  APP_VERSION_PROVIDER_SEARCH,
  PROVIDER_SEARCH_DASHBOARD_FILTERS,
} from "core/consts";
import api from "core/model/api";
import { useSealdContext } from "core/seald/SealdContext";
import { ProviderSearchLoginModal } from "dsl/ecosystems/ProviderSearchLoginModal";
import { useModal, useScrollToTopOnPathChange } from "dsl/hooks";
import { initialStateProviderSearch } from "dsl/organisms/Filters/ProviderSearchApp/initialState";
import {
  ProviderSearchFilterActions,
  ProviderSearchFilters,
} from "dsl/organisms/Filters/ProviderSearchApp/types";
import { persistReducerFunction } from "dsl/organisms/Filters/utils/persistFilterReducer";
import { useEffect } from "react";
import { Outlet, Route, Routes, useOutletContext } from "react-router-dom";
import {
  useConnecterWithLoggedAccount,
  useLoggedCareseeker,
} from "reduxentities/hooks";
import styled from "styled-components";
import { CONTENT_ID } from "../A11ySkipLink";
import { ProviderSearchEnvSwitch } from "./EnvSwitcher";
import { Footer } from "./Footer";
import { Header } from "./Header";
import { usePersistQueryParamProviderSearch } from "./usePersistProviderSearchFilters";

export const AppBarWrapper = styled.main`
  display: flex;
  flex: 1;
  align-items: stretch;
  flex-direction: column;
  min-height: calc(100vh - var(--app-bar-providersearch-height));
`;

export function ProviderSearchRootPage() {
  return (
    <ProviderSearchProvider>
      <RootPage appName={APP_VERSION_PROVIDER_SEARCH}>
        <div className="relative flex min-h-screen w-screen max-w-full flex-col items-stretch overflow-x-hidden print:min-h-0 print:w-full">
          <Outlet />
        </div>
      </RootPage>
    </ProviderSearchProvider>
  );
}

export type ProviderSearchFilterContext = {
  filters: ProviderSearchFilters;
  filtersDrawerOpen: boolean;
  setFilters: React.Dispatch<ProviderSearchFilterActions>;
  setFiltersDrawerOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export function useProviderSearchFilterContext() {
  return useOutletContext<ProviderSearchFilterContext>();
}

export function ProviderSearchHome() {
  const [filtersDrawerOpen, setFiltersDrawerOpen] = useModal();
  const account = useConnecterWithLoggedAccount();
  const careseeker = useLoggedCareseeker();
  const [createEncryptionContext] = useCreateEncryptionContext();
  const [initiateSealdIdentity] = useInitiateSealdIdentity();
  const createSealdGroup = useSealdContext()?.createSealdGroup;

  useScrollToTopOnPathChange();

  useEffect(() => {
    if (account && careseeker) {
      if (createSealdGroup) {
        createSealdGroup({
          createEncryptionContext,
          createSealdAccess: api.crypto.createSealdAccess,
          entity: careseeker,
          entityAccounts: [account],
          entityType: "careseeker",
          initiateSealdIdentity,
          loggedAccount: account,
        });
      } else {
        console.error(
          "could not create seald group in ProviderSearchHome as createSealdGroup not initialised",
        );
      }
    }
  }, [account?.id, careseeker?.id]);

  return (
    <>
      <ProviderSearchEnvSwitch />
      <Header isFiltersDrawerOpen={filtersDrawerOpen} />
      <AppBarWrapper id={CONTENT_ID}>
        <Outlet
          context={{
            filtersDrawerOpen,
            setFiltersDrawerOpen,
          }}
        />
        <ProviderSearchLoginModal />
      </AppBarWrapper>
      <Footer />
    </>
  );
}

export function ProviderSearchFiltersWrapper() {
  const context = useProviderSearchContext();
  const filterContext = useProviderSearchFilterContext();
  const [filters, setFilters] = usePersistQueryParamProviderSearch(
    PROVIDER_SEARCH_DASHBOARD_FILTERS,
    persistReducerFunction,
    initialStateProviderSearch,
    context.config.allowedFederalState,
    context.config.allowedZipcodes,
  );

  return (
    <Outlet
      context={{
        ...filterContext,
        filters,
        setFilters,
      }}
    />
  );
}

export const TestProviderSearchWrapper = ({
  children,
  providerSearchContext,
  providerSearchFilterContext,
}: {
  children: React.ReactNode;
  providerSearchContext?: Partial<ProviderSearchContext>;
  providerSearchFilterContext?: ProviderSearchFilterContext;
}) => {
  if (!providerSearchFilterContext && !providerSearchContext) {
    return <>{children}</>;
  }
  return (
    <TestProviderSearchProvider providerSearchContext={providerSearchContext}>
      <Routes>
        <Route
          element={<Outlet context={providerSearchFilterContext} />}
          path="/"
        >
          <Route element={children} index />
        </Route>
      </Routes>
    </TestProviderSearchProvider>
  );
};
